import { ReactNode, useEffect, useState } from 'react'
import api from '../../../../../helpers/apiRequester'
import { Button, EmptyState, KeyValueItem, KeyValueList, Spinner, StatusPill, cx } from '../../../../../ui'
import { ClaimDetailResponse, formatDate, formatDateTime } from '../shared'
import s from './styles.module.scss'

interface ClaimDetailPanelProps {
    origin: string
    caseRef: string
}

type Row = Record<string, unknown>

const truthy = (v: unknown) => v === 1 || v === '1' || v === true || v === 'YES' || v === 'yes'
const hasValue = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== ''
const text = (v: unknown) => (hasValue(v) ? String(v).trim() : null)
const date = (v: unknown) => (hasValue(v) ? formatDate(String(v)) : null)
const dateTime = (v: unknown) => (hasValue(v) ? formatDateTime(String(v)) : null)

interface ClaimStep {
    label: string
    done: boolean
    when?: unknown
    note?: string | null
}

const humanise = (key: string) =>
    key
        .replace(/_/g, ' ')
        .replace(/\b(cfa|loa|loc|aof|pdf|id|il|cf|dob)\b/gi, (m) => m.toUpperCase())
        .replace(/^./, (c) => c.toUpperCase())

/** Any fields the curated lists did not cover, so nothing the database knows is hidden. */
const leftovers = (row: Row | null | undefined, used: Set<string>): KeyValueItem[] =>
    Object.entries(row ?? {})
        .filter(([key, value]) => !used.has(key) && hasValue(value))
        .map(([key, value]) => ({
            label: humanise(key),
            value: /_date$|_at$/.test(key) ? dateTime(value) : String(value),
        }))

/**
 * Everything the introducer database holds about one claim, shown beneath its
 * row in the Claims table: the agreement itself, how far it has progressed
 * through the pipeline, and the signature links attached to it.
 */
export const ClaimDetailPanel = ({ origin, caseRef }: ClaimDetailPanelProps) => {
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
    const [detail, setDetail] = useState<ClaimDetailResponse | null>(null)
    const [error, setError] = useState<string | undefined>()
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        let cancelled = false
        setStatus('loading')
        api({ url: 'client-claim-detail', data: { origin, case_ref: caseRef } })
            .then((response) => {
                if (cancelled) return
                const body: ClaimDetailResponse = response.data
                if (!body.success) {
                    setError(body.message)
                    setStatus('error')
                    return
                }
                setDetail(body)
                setStatus('ready')
            })
            .catch((err: any) => {
                if (cancelled) return
                setError(err?.response?.data?.message || err?.message)
                setStatus('error')
            })
        return () => {
            cancelled = true
        }
    }, [origin, caseRef, attempt])

    if (status === 'loading') {
        return (
            <div className={s.claimDetail}>
                <Spinner text="Loading claim detail…" />
            </div>
        )
    }

    if (status === 'error' || !detail) {
        return (
            <div className={s.claimDetail}>
                <EmptyState
                    tone="error"
                    title="Could not load this claim"
                    description={error || 'The claim service did not respond.'}
                    action={
                        <Button size="sm" onClick={() => setAttempt((n) => n + 1)}>
                            Retry
                        </Button>
                    }
                />
            </div>
        )
    }

    if (!detail.supported) {
        return (
            <div className={s.claimDetail}>
                <p className={s.cardText}>
                    {detail.message || 'Detailed claim data is not available for this record.'}
                </p>
            </div>
        )
    }

    const lead = detail.lead ?? {}
    const state = detail.state ?? {}

    const usedLead = new Set([
        'case_ref',
        'base_claim',
        'lender',
        'lender_type',
        'policy_number',
        'account_type',
        'agreement_status',
        'start_date',
        'length_of_contract',
        'monthly_repayment',
        'total_amount_payable',
        'starting_balance',
        'commission',
        'stat_interest',
        'quantum_value',
        'total_days',
        'total_years',
        'pdf_claim',
        'origin',
        'lead_source',
        'created_at',
        'updated_at',
    ])
    const usedState = new Set([
        'cfa_signed',
        'cfa_signed_date',
        'full_credit_check_request',
        'full_credit_check_request_date',
        'full_credit_check_complete',
        'full_credit_check_complete_date',
        'policy_number_assigned',
        'policy_number_assigned_date',
        'documents_populated',
        'full_claim_transferred',
        'full_claim_transferred_date',
        'funder_allocated',
        'cancelled',
        'cancellation_date',
        'cancellation_reason',
        'created_at',
        'updated_at',
    ])

    // Headline flags -------------------------------------------------------------
    const cancelled = truthy(state.cancelled)
    const flags: ReactNode[] = [
        cancelled ? (
            <StatusPill key="status" tone="bad" dot>
                Cancelled
                {hasValue(state.cancellation_date) ? ` · ${formatDate(String(state.cancellation_date))}` : ''}
                {hasValue(state.cancellation_reason) ? ` · ${state.cancellation_reason}` : ''}
            </StatusPill>
        ) : (
            <StatusPill key="status" tone="good" dot>
                Active
            </StatusPill>
        ),
        truthy(lead.base_claim) && (
            <StatusPill key="base" tone="accent">
                Base claim
            </StatusPill>
        ),
        hasValue(lead.pdf_claim) && (
            <StatusPill key="pdf" tone={truthy(lead.pdf_claim) ? 'info' : 'neutral'}>
                {truthy(lead.pdf_claim) ? 'Found on credit report' : 'Not on credit report'}
            </StatusPill>
        ),
        hasValue(lead.agreement_status) && (
            <StatusPill key="agreement">Agreement {String(lead.agreement_status)}</StatusPill>
        ),
    ].filter(Boolean)

    // The agreement itself ------------------------------------------------------
    const agreement: KeyValueItem[] = [
        { label: 'Lender', value: text(lead.lender) },
        { label: 'Lender type', value: text(lead.lender_type) },
        { label: 'Account type', value: text(lead.account_type) },
        { label: 'Agreement number', value: text(lead.policy_number), mono: true },
        { label: 'Start date', value: date(lead.start_date) },
        {
            label: 'Term',
            value: text(lead.length_of_contract) ? `${text(lead.length_of_contract)} months` : null,
        },
        {
            label: 'Duration to date',
            value: hasValue(lead.total_days)
                ? `${lead.total_days} days${hasValue(lead.total_years) ? ` (${lead.total_years} yrs)` : ''}`
                : null,
        },
    ]

    // Money ---------------------------------------------------------------------
    const finance: KeyValueItem[] = [
        { label: 'Starting balance', value: text(lead.starting_balance) },
        { label: 'Monthly repayment', value: text(lead.monthly_repayment) },
        { label: 'Total payable', value: text(lead.total_amount_payable) },
        { label: 'Commission', value: text(lead.commission) },
        { label: 'Statutory interest', value: text(lead.stat_interest) },
        { label: 'Quantum value', value: text(lead.quantum_value) },
    ]

    // Record housekeeping + anything the curated lists did not cover --------------
    const record: KeyValueItem[] = [
        { label: 'Case ref', value: text(lead.case_ref), mono: true },
        { label: 'Lead origin', value: text(lead.origin) ?? text(lead.lead_source), mono: true },
        { label: 'Claim created', value: dateTime(lead.created_at) },
        { label: 'Claim updated', value: dateTime(lead.updated_at) },
        { label: 'State updated', value: dateTime(state.updated_at) },
        ...leftovers(lead, usedLead),
        ...leftovers(state, usedState),
    ]

    // Pipeline, in the order the claim moves through it -------------------------
    const steps: ClaimStep[] = detail.state
        ? [
              { label: 'CFA signed', done: truthy(state.cfa_signed), when: state.cfa_signed_date },
              {
                  label: 'Credit check requested',
                  done: hasValue(state.full_credit_check_request),
                  when: state.full_credit_check_request_date,
                  note: text(state.full_credit_check_request),
              },
              {
                  label: 'Credit check complete',
                  done: truthy(state.full_credit_check_complete),
                  when: state.full_credit_check_complete_date,
              },
              {
                  label: 'Agreement number assigned',
                  done: truthy(state.policy_number_assigned),
                  when: state.policy_number_assigned_date,
              },
              { label: 'Documents populated', done: truthy(state.documents_populated) },
              {
                  label: 'Claim transferred',
                  done: truthy(state.full_claim_transferred),
                  when: state.full_claim_transferred_date,
              },
              { label: 'Funder allocated', done: truthy(state.funder_allocated) },
          ]
        : []
    const completed = steps.filter((step) => step.done).length

    return (
        <div className={s.claimDetail}>
            <div className={s.claimFlags}>{flags}</div>
            <div className={s.claimDetailGrid}>
                <section className={s.claimDetailSection}>
                    <h4 className={s.claimDetailTitle}>Agreement</h4>
                    <KeyValueList items={agreement} />
                </section>
                <section className={s.claimDetailSection}>
                    <h4 className={s.claimDetailTitle}>Finance &amp; valuation</h4>
                    <KeyValueList items={finance} />
                </section>
                <section className={s.claimDetailSection}>
                    <h4 className={s.claimDetailTitle}>
                        Progress
                        {steps.length ? (
                            <span className={s.claimDetailTitleMeta}>
                                {completed}/{steps.length}
                            </span>
                        ) : null}
                    </h4>
                    {steps.length ? (
                        <ol className={s.claimSteps}>
                            {steps.map((step) => (
                                <li key={step.label} className={cx(s.claimStep, step.done && s.claimStepDone)}>
                                    <span className={s.claimStepDot} aria-hidden="true" />
                                    <span className={s.claimStepLabel}>
                                        {step.label}
                                        {step.note && step.note.toUpperCase() !== 'SENT' && (
                                            <span className={s.muted}> · {step.note}</span>
                                        )}
                                    </span>
                                    <span className={s.claimStepWhen}>
                                        {step.done
                                            ? hasValue(step.when)
                                                ? formatDate(String(step.when))
                                                : 'Done'
                                            : 'Pending'}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <p className={s.cardText}>No workflow state has been recorded for this claim.</p>
                    )}
                </section>
                <section className={cx(s.claimDetailSection, s.claimDetailWide)}>
                    <h4 className={s.claimDetailTitle}>Record</h4>
                    <KeyValueList items={record} columns />
                </section>
            </div>
        </div>
    )
}
